import { LayoutResponseType } from '@app-store'
import { useMemo } from 'react'
import { ElementType } from '../types/layout.types'
import {
  buildBehaviorCategoryFilter,
  buildBehaviorLookupFilter,
  getEffectiveDisabled,
  getEffectiveDateBound,
  getEffectiveHidden,
  getEffectiveRequired,
} from './formBehavior.utils'
import { triggerFormBehaviorEvaluation } from './formBehaviorTrigger.registry'
import { useFormBehaviorState } from './useFormBehaviorState'

export const getFormBehaviorRuleKey = (element: ElementType): string => {
  const fieldKey =
    element.keyName?.trim() ||
    element.meta?.field_name?.trim() ||
    element.meta?.originalKeyName?.trim() ||
    ''
  if (fieldKey) return fieldKey
  return element.elementName?.trim() || ''
}

export const useFormBehaviorFieldOverrides = (
  fullLayout: LayoutResponseType | undefined,
  element: ElementType,
  baseDisabled: boolean,
  layoutRequired?: boolean | null
) => {
  const layoutUid = fullLayout?.uid
  const formBehavior = useFormBehaviorState(layoutUid)
  const ruleKey = getFormBehaviorRuleKey(element)
  const hasFormRules = !!(
    layoutUid && (fullLayout?.layout_data?.meta?.form_rules?.length ?? 0) > 0
  )

  const allowedIds = formBehavior.lookupAllowedIds[ruleKey]
  const allowedLabels = formBehavior.lookupAllowedLabels[ruleKey]
  const hasIds = Object.prototype.hasOwnProperty.call(
    formBehavior.lookupAllowedIds,
    ruleKey
  )
  const hasLabels = Object.prototype.hasOwnProperty.call(
    formBehavior.lookupAllowedLabels,
    ruleKey
  )

  const behaviorLookupFilter = useMemo(
    () => buildBehaviorLookupFilter(formBehavior, ruleKey, hasFormRules),
    // Primitive/array refs from Redux stay stable until form behavior updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasFormRules, ruleKey, hasIds, hasLabels, allowedIds, allowedLabels]
  )

  const categoryAllowedIds = formBehavior.categoryAllowedIds[ruleKey]
  const categoryAllowedLabels = formBehavior.categoryAllowedLabels[ruleKey]
  const hasCategoryIds = Object.prototype.hasOwnProperty.call(
    formBehavior.categoryAllowedIds,
    ruleKey
  )
  const hasCategoryLabels = Object.prototype.hasOwnProperty.call(
    formBehavior.categoryAllowedLabels,
    ruleKey
  )

  const behaviorCategoryFilter = useMemo(
    () => buildBehaviorCategoryFilter(formBehavior, ruleKey, hasFormRules),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasFormRules,
      ruleKey,
      hasCategoryIds,
      hasCategoryLabels,
      categoryAllowedIds,
      categoryAllowedLabels,
    ]
  )

  return {
    ruleKey,
    hasFormRules,
    effectiveDisabled: getEffectiveDisabled(
      ruleKey,
      baseDisabled,
      formBehavior.disabledOverrides
    ),
    effectiveRequired: getEffectiveRequired(
      ruleKey,
      layoutRequired,
      formBehavior.requiredOverrides
    ),
    effectiveHidden: getEffectiveHidden(
      ruleKey,
      element.is_hidden,
      formBehavior.hiddenOverrides
    ),
    behaviorLookupFilter,
    behaviorCategoryFilter,
    effectiveMinDate: getEffectiveDateBound(
      ruleKey,
      '',
      formBehavior.minDateOverrides
    ),
    effectiveMaxDate: getEffectiveDateBound(
      ruleKey,
      '',
      formBehavior.maxDateOverrides
    ),
    triggerFieldChange: () => {
      if (layoutUid && hasFormRules) {
        triggerFormBehaviorEvaluation(layoutUid, 'change', ruleKey)
      }
    },
  }
}
